import { useMemo } from 'react'
import { useId } from 'react'
import styles from './PathType.module.scss'
import classNames from 'classnames'
import { Price } from '@ds'

export enum PathVariant {
  PremiumActive = 'premium-active',
  PremiumInactive = 'premium-inactive',
  Free = 'free',
}

type Props = {
  price?: number
  variant: PathVariant
  onClick?: () => void
}

export const PathType = ({ price, variant, onClick }: Props) => {
  const premiumActiveFillGradientId = useId()
  const premiumActiveStrokeGradientId = useId()
  const premiumActiveTextGradientId = useId()
  const premiumInactiveStrokeGradientId = useId()
  const premiumInactiveTextGradientId = useId()

  const freeFillGradientId = useId()
  const freeTextGradientId = useId()

  const { strokeGradientId, fillGradientId, textGradientId } = useMemo(() => {
    switch (variant) {
      case PathVariant.PremiumActive:
        return {
          strokeGradientId: premiumActiveStrokeGradientId,
          fillGradientId: premiumActiveFillGradientId,
          textGradientId: premiumActiveTextGradientId,
        }
      case PathVariant.PremiumInactive:
        return {
          strokeGradientId: premiumInactiveStrokeGradientId,
          fillGradientId: null,
          textGradientId: premiumInactiveTextGradientId,
        }
      case PathVariant.Free:
        return {
          strokeGradientId: null,
          fillGradientId: freeFillGradientId,
          textGradientId: freeTextGradientId,
        }
    }
  }, [variant])

  return (
    <div
      className={styles.pathType}
      onClick={onClick}
    >
      <svg
        viewBox="0 0 102 34"
        fill="none"
      >
        <path
          d="M1.75 15.33C5.02 11.04 6.93 5.88 7.26 0.50H94.26C94.59 5.88 96.51 11.04 99.78 15.33L100.84 16.73L99.78 18.13C96.51 22.42 94.59 27.59 94.26 32.96H7.26C6.93 27.59 5.02 22.42 1.75 18.13L0.68 16.73L1.75 15.33Z"
          stroke={`url(#${strokeGradientId})`}
          fill={fillGradientId ? `url(#${fillGradientId})` : 'black'}
          strokeMiterlimit="3.14"
        />
        <text
          fill={`url(#${textGradientId})`}
          fontFamily="var(--font-main)"
          fontSize="16"
          letterSpacing="-0.05em"
          x="50%"
          y="50%"
          textAnchor="middle"
          alignmentBaseline="middle"
        >
          {variant === PathVariant.Free ? 'free' : 'premium'}
        </text>
        <defs>
          <linearGradient
            id={premiumActiveFillGradientId}
            x1="48.50"
            y1="-14.00"
            x2="85.50"
            y2="44.50"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#D9D9D9" />
            <stop offset="1" />
          </linearGradient>
          <linearGradient
            id={premiumActiveStrokeGradientId}
            x1="106.50"
            y1="13.00"
            x2="2.50"
            y2="15.50"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="0.43" stopColor="#767676" />
            <stop offset="1" stopColor="white" />
          </linearGradient>
          <linearGradient
            id={premiumInactiveStrokeGradientId}
            x1="65.62"
            y1="52.93"
            x2="76.54"
            y2="-6.49"
            gradientUnits="userSpaceOnUse"
          >
            <stop />
            <stop offset="1" stopColor="#7F7F7F" />
          </linearGradient>
          <linearGradient
            id={freeFillGradientId}
            x1="71.89"
            y1="66.16"
            x2="101.58"
            y2="-63.28"
            gradientUnits="userSpaceOnUse"
          >
            <stop />
            <stop offset="1" stopColor="#7F7F7F" />
          </linearGradient>
          <linearGradient
            id={freeTextGradientId}
            x1="51.46"
            y1="5.46"
            x2="51.46"
            y2="26.46"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="#4F4F4F" />
          </linearGradient>
          <linearGradient
            id={premiumInactiveTextGradientId}
            x1="51.36"
            y1="5.46"
            x2="51.36"
            y2="26.46"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="1" stopColor="#4F4F4F" />
          </linearGradient>
          <linearGradient
            id={premiumActiveTextGradientId}
            x1="51.36"
            y1="5.46"
            x2="51.36"
            y2="26.46"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#646464" />
            <stop offset="0.80" />
          </linearGradient>
        </defs>
      </svg>
      {variant === PathVariant.PremiumInactive && price && <div className={styles.priceContainer}>
        <Price
          dark
          value={price}
          className={styles.price}
        />
      </div>}
    </div>
  )
}
